/*
  Panneau de cartes de marques du drawer.

  À partir de 1024px avec un pointeur fin, le survol du déclencheur ouvre un
  panneau pleine largeur de fenêtre placé derrière le drawer. En dessous, ou au
  tactile, le panneau se comporte comme un sous-menu classique ouvert au clic.

  Le panneau vit hors de <menu-drawer>: le piège de focus du thème ramasse tous
  les liens de son conteneur sans filtrer la visibilité, et les cartes fermées y
  casseraient le bouclage du Tab. Le clavier est donc géré ici.
*/

// Les panneaux écrivent tous la même mesure sur documentElement: un seul suffit.
let drawerTopTracker = null;

class BrandFlyout extends HTMLElement {
  constructor() {
    super();

    this.openDelay = 60;
    this.closeDelay = 250;
    this.lastPointerType = '';
    this.openTimer = null;
    this.closeTimer = null;
    this.resizeTimer = null;
    this.trackingFrame = null;
    this.lastTop = null;
    this.lastListTop = null;
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  get hoverEnabled() {
    return this.finePointer.matches && this.desktop.matches;
  }

  get cards() {
    return Array.from(this.querySelectorAll('[data-brands-level]:not([hidden]) .drawer__brand-card'));
  }

  get levels() {
    return Array.from(this.querySelectorAll('[data-brands-level]'));
  }

  connectedCallback() {
    // Rendu dans la section header, le panneau hérite de son contexte
    // d'empilement et ne peut plus s'ordonner face au drawer. On le remonte à la
    // racine, comme le thème le fait déjà pour ses propres drawers.
    if (this.parentElement !== document.body) {
      // this est déjà dans le sous-arbre de body, d'où le filtrage explicite:
      // un re-rendu de la section dans l'éditeur laisserait sinon un panneau
      // orphelin qui se disputerait la classe has-brand-flyout avec le vivant.
      document.body.querySelectorAll(`brand-flyout#${this.id}`).forEach((node) => {
        if (node !== this) node.remove();
      });

      document.body.appendChild(this);
      return;
    }

    this.trigger = document.getElementById(this.dataset.trigger);
    if (!this.trigger) return;

    this.drawer = document.getElementById('MenuDrawer');
    this.parentMenu = this.drawer ? this.drawer.querySelector('.drawer__menu') : null;
    this.desktop = window.matchMedia('(min-width: 1024px)');
    this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.abortController = new AbortController();

    const options = { signal: this.abortController.signal };

    this.trigger.addEventListener('pointerdown', (event) => { this.lastPointerType = event.pointerType; }, options);
    this.trigger.addEventListener('click', this.onTriggerClick.bind(this), options);
    this.trigger.addEventListener('mouseenter', this.onPointerEnter.bind(this), options);
    this.trigger.addEventListener('mouseleave', this.onPointerLeave.bind(this), options);
    this.trigger.addEventListener('focusout', this.onFocusOut.bind(this), options);
    this.addEventListener('mouseenter', this.onPointerEnter.bind(this), options);
    this.addEventListener('mouseleave', this.onPointerLeave.bind(this), options);
    this.addEventListener('focusout', this.onFocusOut.bind(this), options);
    this.addEventListener('keydown', this.onPanelKeyDown.bind(this), options);

    // Sous 1024px, ce bouton est le seul chemin de retour: il remonte d'abord
    // d'un niveau, et ne referme le panneau qu'une fois au premier niveau.
    const closeButton = this.querySelector('[data-brands-close]');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        const actif = this.querySelector('[data-brands-level]:not([hidden])');

        if (actif && actif.getAttribute('data-brands-level') !== 'root') {
          this.showLevel('root');
          return;
        }

        this.close();
      }, options);
    }

    this.addEventListener('click', this.onLevelClick.bind(this), options);

    // En capture, pour couper l'Échap avant le handler du drawer (ModalElement
    // écoute keyup sur document): le premier Échap ne referme que le panneau.
    document.addEventListener('keyup', this.onKeyUp.bind(this), { ...options, capture: true });
    this.desktop.addEventListener('change', () => { this.close(); this.updateDrawerTop(); }, options);
    window.addEventListener('resize', this.onResize.bind(this), options);

    this.updateDrawerTop();

    if (this.drawer) {
      // 'toggle' part dès que le drawer commence à se fermer, donc les deux
      // disparaissent ensemble; 'drawer:afterHide' reste le filet de sécurité.
      this.drawer.addEventListener('toggle', this.onDrawerToggle.bind(this), options);
      this.drawer.addEventListener('drawer:afterHide', () => this.close(), options);

      // Ouvrir un autre sous-menu referme le panneau.
      this.drawer.querySelectorAll('.drawer__menu summary').forEach((summary) => {
        summary.addEventListener('click', () => this.close(), options);
      });

      // Le panneau couvre toute la fenêtre: sortir du déclencheur ne suffit pas
      // à décider d'une fermeture. C'est le survol d'une autre entrée du menu qui
      // l'arme, et chaque entrée traversée réarme le délai, de sorte qu'un
      // mouvement en diagonale vers les cartes ne referme rien.
      this.drawer.querySelectorAll('.drawer__menu > li').forEach((item) => {
        if (item.contains(this.trigger)) return;
        item.addEventListener('mouseenter', this.onPointerLeave.bind(this), options);
      });
    }
  }

  disconnectedCallback() {
    clearTimeout(this.levelSwitchTimer);
    this.clearTimers();
    clearTimeout(this.resizeTimer);
    this.stopTracking();
    this.abortController?.abort();
  }

  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.updateDrawerTop(), 100);
  }

  clearTimers() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  }


  onDrawerToggle() {
    if (this.drawer.hasAttribute('open')) {
      this.updateDrawerTop();
      this.startTracking();
    }
    else {
      this.stopTracking();
      this.close();
    }
  }

  /*
    La page reste scrollable pendant que le drawer est ouvert, et le header
    collant se déplace par transition CSS. Un écouteur de scroll raterait les
    images intermédiaires de cette transition, d'où le suivi image par image tant
    que le drawer est ouvert. Rien n'est écrit tant que la valeur ne bouge pas.
  */
  startTracking() {
    if (this.trackingFrame) return;
    if (drawerTopTracker && drawerTopTracker !== this) return;

    drawerTopTracker = this;

    const step = () => {
      this.updateDrawerTop();
      this.trackingFrame = requestAnimationFrame(step);
    };

    this.trackingFrame = requestAnimationFrame(step);
  }

  stopTracking() {
    if (!this.trackingFrame) return;

    cancelAnimationFrame(this.trackingFrame);
    this.trackingFrame = null;
    if (drawerTopTracker === this) drawerTopTracker = null;
  }

  /*
    Sur ordinateur, le drawer et le panneau se posent sous la barre du header.
    Sa position dépend de la barre d'annonce et de l'état du header collant, donc
    elle se mesure à l'ouverture plutôt que de se déduire d'une hauteur fixe.
  */
  updateDrawerTop() {
    if (!this.desktop.matches) {
      document.documentElement.style.removeProperty('--menu-drawer-top');
      document.documentElement.style.removeProperty('--menu-drawer-list-top');
      this.lastTop = null;
      this.lastListTop = null;
      return;
    }

    const header = document.querySelector('.header-section header.header');
    const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;

    if (top !== this.lastTop) {
      document.documentElement.style.setProperty('--menu-drawer-top', `${top}px`);
      this.lastTop = top;
    }

    // La première ligne de cartes s'aligne sur la première entrée du menu, dont
    // la hauteur du bandeau au-dessus dépend de ce que le drawer y affiche.
    const firstItem = this.drawer && !this.drawer.hidden && this.drawer.querySelector('.drawer__menu > li');
    if (!firstItem) return;

    const listTop = Math.max(0, Math.round(firstItem.getBoundingClientRect().top - top));

    if (listTop !== this.lastListTop) {
      document.documentElement.style.setProperty('--menu-drawer-list-top', `${listTop}px`);
      this.lastListTop = listTop;
    }
  }

  onTriggerClick(event) {
    event.preventDefault();

    // Souris sur desktop: c'est le survol qui pilote. On ne garde le clic que
    // pour le clavier (event.detail vaut 0) et pour le doigt, y compris sur un
    // écran tactile assez large pour que le survol soit par ailleurs actif.
    if (this.hoverEnabled && event.detail !== 0 && this.lastPointerType !== 'touch') {
      // Le survol ouvre le panneau, le clic souris suit le lien de l'entrée.
      const href = this.trigger.dataset.href;
      if (href && href !== '#') {
        // Un vrai clic sur un lien, pour passer par la même navigation que les
        // autres liens du thème (et de l'éditeur) plutôt que par location.href.
        const link = document.createElement('a');
        link.href = href;
        link.hidden = true;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      return;
    }

    if (this.isOpen) {
      this.close();
    }
    else {
      this.open();
      // Ouverture au clavier: le focus entre dans le panneau, qui suit le
      // déclencheur dans l'ordre de lecture mais pas dans l'ordre du document.
      if (event.detail === 0) this.cards[0]?.focus();
    }
  }

  onPointerEnter() {
    if (!this.hoverEnabled) return;

    this.clearTimers();
    this.openTimer = setTimeout(() => this.open(), this.openDelay);
  }

  /*
    Chaque appel réarme le délai depuis zéro: traverser plusieurs entrées du menu
    pour rejoindre les cartes ne doit pas cumuler les minuteurs. Tant que le focus
    clavier est dans le panneau, la souris ne décide plus rien: sinon un panneau
    ouvert à l'Entrée se refermerait seul parce que le curseur est resté posé sur
    une autre entrée du menu.
  */
  onPointerLeave() {
    if (!this.hoverEnabled || this.contains(document.activeElement)) return;

    this.clearTimers();
    this.closeTimer = setTimeout(() => this.close(), this.closeDelay);
  }

  onFocusOut(event) {
    if (this.levelSwitch) return;
    if (this.contains(event.relatedTarget) || event.relatedTarget === this.trigger) return;
    if (this.matches(':hover') || this.trigger.matches(':hover')) return;

    this.close();
  }

  // Tant que le panneau est ouvert, le Tab boucle entre ses cartes et le
  // déclencheur, sinon il partirait dans le drawer resté derrière.
  onPanelKeyDown(event) {
    if (event.key !== 'Tab' || !this.isOpen) return;

    const cards = this.cards;
    if (cards.length === 0) return;

    if (event.shiftKey && event.target === cards[0]) {
      event.preventDefault();
      this.trigger.focus();
    }
    else if (!event.shiftKey && event.target === cards[cards.length - 1]) {
      event.preventDefault();
      cards[0].focus();
    }
  }

  /*
    Les sous-menus de sous-menus sont déjà rendus, chacun dans son niveau. Passer
    de l'un à l'autre revient donc à changer celui qui est affiché, sans rien
    charger. La classe is-entering relance l'animation d'apparition à chaque
    bascule, d'où le retrait puis la relecture forcée de la mise en page.
  */
  showLevel(name) {
    /*
      La carte cliquée appartient au niveau qu'on masque. Sans reprise du focus,
      le navigateur le renvoie au document, onFocusOut voit sortir le focus du
      panneau et le referme aussitôt. On le repose donc sur le niveau affiché.
    */
    const avaitLeFocus = this.contains(document.activeElement);
    let affiche = null;

    /*
      Masquer un niveau rend non focalisable la carte qu'on vient d'activer, et le
      navigateur relâche le focus. onFocusOut y lit une sortie du panneau et le
      referme. Le drapeau couvre le temps que le focus se repose.
    */
    this.levelSwitch = true;
    clearTimeout(this.levelSwitchTimer);
    this.levelSwitchTimer = setTimeout(() => { this.levelSwitch = false; }, 250);

    this.levels.forEach((level) => {
      const actif = level.getAttribute('data-brands-level') === name;

      level.hidden = !actif;
      level.classList.remove('is-entering');

      if (actif) {
        affiche = level;
        void level.offsetWidth;
        level.classList.add('is-entering');
      }
    });

    const scroll = this.querySelector('[data-brands-scroll]');
    if (scroll) scroll.scrollTop = 0;

    if (avaitLeFocus && affiche) {
      const cible = affiche.querySelector('[data-brands-back], .drawer__brand-card');
      if (cible) cible.focus({ preventScroll: true });
    }
  }

  onLevelClick(event) {
    const opener = event.target.closest('[data-brands-open]');
    if (opener) {
      event.preventDefault();
      this.showLevel(opener.getAttribute('data-brands-open'));
      return;
    }

    if (event.target.closest('[data-brands-back]')) {
      event.preventDefault();
      this.showLevel('root');
    }
  }

  onKeyUp(event) {
    if (event.key !== 'Escape' || !this.isOpen) return;

    event.stopPropagation();
    this.close();
    this.trigger.focus();
  }

  open() {
    this.clearTimers();
    if (this.isOpen) return;

    /*
      Un seul panneau à la fois. Ils partagent la classe has-brand-flyout, qui
      rend le voile du tiroir transparent aux clics: deux panneaux ouverts, et
      c'est le premier refermé qui la retire sous les pieds du second.

      Quand on passe d'un panneau à l'autre, les deux fondus se croisent et le
      cadre s'efface à moitié au milieu du geste. Le temps de la bascule, les deux
      renoncent donc à leur transition: le cadre reste posé, et seul le contenu
      s'anime.
    */
    const autres = Array.from(document.querySelectorAll('brand-flyout.is-open')).filter((node) => node !== this);
    const bascule = autres.length > 0;

    autres.forEach((node) => {
      if (bascule) node.classList.add('is-switching');
      node.close();
    });

    if (bascule) this.classList.add('is-switching');

    this.showLevel('root');
    this.classList.add('is-open');

    // Sans cette lecture, le navigateur ne calcule les styles qu'une fois les
    // deux classes reposées à l'image suivante, et la transition repart: le
    // panneau entrant se remettrait à monter en fondu sur un panneau déjà parti.
    if (bascule) void this.offsetWidth;
    document.documentElement.classList.add('has-brand-flyout');
    this.trigger.setAttribute('aria-expanded', 'true');

    if (bascule) {
      requestAnimationFrame(() => {
        this.classList.remove('is-switching');
        autres.forEach((node) => node.classList.remove('is-switching'));
      });
    }

    if (!this.desktop.matches && this.parentMenu) {
      this.parentMenu.classList.add('active');
    }
  }

  close() {
    this.clearTimers();
    if (!this.isOpen) return;

    this.classList.remove('is-open');

    // is-open vient d'être retirée, la requête ne se voit donc pas elle-même.
    if (!document.querySelector('brand-flyout.is-open')) {
      document.documentElement.classList.remove('has-brand-flyout');
    }
    this.trigger.setAttribute('aria-expanded', 'false');

    if (!this.desktop.matches && this.parentMenu) {
      this.parentMenu.classList.remove('active');
    }
  }
}

if (!customElements.get('brand-flyout')) {
  customElements.define('brand-flyout', BrandFlyout);
}
